import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Flex,
    Text,
    Button,
    Image,
    Icon,
    List,
    ListItem,
    ListIcon,
    Divider,
} from '@chakra-ui/react';
import { motion, useMotionValue, useTransform, animate, useInView } from 'framer-motion';
import { FaShip, FaShoppingBag, FaUniversity, FaWeixin, FaTelegram, FaWhatsapp, FaEnvelope, FaGlobeAsia, FaBoxOpen, FaUserTie, FaCheckCircle } from 'react-icons/fa';

import LogoObj from './assets/logo.png';
import ShipsImg from './assets/ships.png';
import MapImg from './assets/map.png';

const AnimatedCounter = ({ from = 0, to, duration = 2 }) => {
    const count = useMotionValue(from);
    const rounded = useTransform(count, (latest) => Math.round(latest));
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true });

    useEffect(() => {
        if (isInView) {
            const controls = animate(count, to, { duration });
            return controls.stop;
        }
    }, [isInView, count, to, duration]);

    return <motion.span ref={ref}>{rounded}</motion.span>;
};

const content = {
    en: {
        hero: {
            headline: "Expert in Sourcing & Shipping from China",
            subheadline: "Simplifying your business logistics. I help clients secure high-quality products at competitive prices with timely delivery.",
            cta: "Start Your Order"
        },
        stats: [
            { value: "750+", numeric: 750, suffix: "+", label: "Clients", icon: FaUserTie },
            { value: "3205", numeric: 3205, suffix: "", label: "Shipments", icon: FaBoxOpen },
            { value: "3+", numeric: 3, suffix: "+", label: "Years Exp", icon: FaGlobeAsia },
            { value: "7+", numeric: 7, suffix: "+", label: "Countries", icon: FaShip }
        ],
        services: {
            title: "Our Services",
            list: [
                {
                    title: "Shipping Services",
                    items: [
                        "Air & Sea Shipping",
                        "Shipment Consolidation",
                        "Shipment Tracking"
                    ],
                    icon: FaShip
                },
                {
                    title: "Purchase Management",
                    items: [
                        "Supplier Negotiation",
                        "Invoice Follow-up",
                        "Quality Control"
                    ],
                    icon: FaShoppingBag
                },
                {
                    title: "Education Services",
                    items: [
                        "University Registration",
                        "Admission Support",
                        "Student Assistance"
                    ],
                    icon: FaUniversity
                }
            ]
        },
        whyUs: {
            title: "Why Choose Us?",
            text: "Access to over 30 cities and 5,000+ factories. We make sourcing simple, reliable, and stress-free.",
            badges: ["Reliable", "Fast", "Verified"],
            subtitle: "Global Reach"
        },
        footer: {
            title: "Contact with me",
            rights: "© Daneil. All rights reserved."
        }
    },
    ar: {
        hero: {
            headline: "خبير في التوريد والشحن من الصين",
            subheadline: "نجعل تجارتك أسهل. أساعدك في تأمين منتجات عالية الجودة بأسعار تنافسية مع ضمان الوصول في الوقت المحدد.",
            cta: "ابدأ طلبك الآن"
        },
        stats: [
            { value: "750+", numeric: 750, suffix: "+", label: "عملاء", icon: FaUserTie },
            { value: "3205", numeric: 3205, suffix: "", label: "شحنة", icon: FaBoxOpen },
            { value: "3+", numeric: 3, suffix: "+", label: "سنوات خبرة", icon: FaGlobeAsia },
            { value: "7+", numeric: 7, suffix: "+", label: "دولة", icon: FaShip }
        ],
        services: {
            title: "خدماتنا",
            list: [
                {
                    title: "خدمات الشحن",
                    items: [
                        "الشحن الجوي والبحري",
                        "تجميع الشحنات",
                        "تتبع الشحنات"
                    ],
                    icon: FaShip
                },
                {
                    title: "إدارة المشتريات",
                    items: [
                        "التفاوض مع الموردين",
                        "متابعة الفواتير",
                        "مراقبة الجودة"
                    ],
                    icon: FaShoppingBag
                },
                {
                    title: "خدمات تعليمية",
                    items: [
                        "التسجيل في الجامعات",
                        "دعم القبول",
                        "مساعدة الطلاب"
                    ],
                    icon: FaUniversity
                }
            ]
        },
        whyUs: {
            title: "لماذا نحن؟",
            text: "وصول لأكثر من 30 مدينة و 5000 مصنع. نجعل الاستيراد من الصين بسيطًا ومضمونًا.",
            badges: ["موثوق", "سريع", "معتمد"],
            subtitle: "وصول عالمي"
        },
        footer: {
            title: "تواصل معي",
            rights: "© دانيال. جميع الحقوق محفوظة."
        }
    }
};

const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2,
            delayChildren: 0.1
        }
    }
};

const MotionBox = motion(Box);
const MotionText = motion(Text);

const Home = () => {
    const [lang, setLang] = useState('en'); // 'en' | 'ar'
    const [pageContent, setPageContent] = useState(content);
    const [siteConfig, setSiteConfig] = useState({
        name: "Daneil",
        email: "",
        whatsapp: "#",
        wechat: "#",
        telegram: "#"
    });
    const isAr = lang === 'ar';
    const t = pageContent[lang];

    useEffect(() => {
        fetch('https://api.npoint.io/451aca014e20a01b0b41')
            .then(res => res.json())
            .then(data => {
                setPageContent(prevContent => ({
                    ...prevContent,
                    en: {
                        ...prevContent.en,
                        hero: {
                            ...prevContent.en.hero,
                            subheadline: data.description_en || prevContent.en.hero.subheadline
                        },
                        whyUs: {
                            ...prevContent.en.whyUs,
                            text: data.why_en || prevContent.en.whyUs.text
                        }
                    },
                    ar: {
                        ...prevContent.ar,
                        hero: {
                            ...prevContent.ar.hero,
                            subheadline: data.description_ar || prevContent.ar.hero.subheadline
                        },
                        whyUs: {
                            ...prevContent.ar.whyUs,
                            text: data.why_ar || prevContent.ar.whyUs.text
                        }
                    }
                }));

                setSiteConfig({
                    name: data.name || "Daneil",
                    email: data.email || "",
                    whatsapp: data.whatsapp_link || "#",
                    wechat: data.wechat_link || "#",
                    telegram: data.telegram_link || "#"
                });
            })
            .catch(err => console.error("Failed to fetch content:", err));
    }, []);

    const toggleLang = (newLang) => setLang(newLang);

    const bgGradient = "linear-gradient(180deg, #0a192f 0%, #112240 100%)";
    const glassBg = "rgba(17, 34, 64, 0.75)";
    const glassBorder = "1px solid rgba(255, 255, 255, 0.1)";
    const accentColor = "#b2ff6b";
    const darkTextColor = "#0a192f";

    const contactActions = [
        { label: isAr ? "محادثة واتساب" : "Chat on WhatsApp", icon: FaWhatsapp, href: siteConfig.whatsapp },
        { label: isAr ? "وي تشات" : "WeChat", icon: FaWeixin, href: siteConfig.wechat },
        { label: isAr ? "إرسال إيميل" : "Send Email", icon: FaEnvelope, href: siteConfig.email ? `mailto:${siteConfig.email}` : "#" },
        { label: isAr ? "تيليجرام" : "Telegram", icon: FaTelegram, href: siteConfig.telegram },
    ];

    return (
        <Box dir={isAr ? 'rtl' : 'ltr'} bg={bgGradient} minH="100vh" w="100%" overflowX="hidden" fontFamily="'Cairo', sans-serif">

            <Flex
                as="nav"
                position="fixed"
                top="0"
                left="0"
                right="0"
                py={4}
                px={{ base: 4, md: 12 }}
                align="center"
                justify="space-between"
                zIndex="1000"
                backdropFilter="blur(12px)"
                borderBottom="1px solid rgba(255,255,255,0.05)"
                transition="all 0.3s ease"
                dir="ltr"
            >
                <Flex cursor="pointer" _hover={{ opacity: 0.8 }} gap={3}>
                    <Image src={LogoObj} alt="Daneil Logo" h={{ base: "40px", md: "50px" }} objectFit="contain" filter="brightness(1.2)" />
                    <Text mt={{ base: '9px', md: '10px' }} bgGradient={`linear(to-r, ${accentColor}, teal.300)`} bgClip="text" fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" letterSpacing="wide">
                        {siteConfig.name}
                    </Text>
                </Flex>

                <Flex
                    bg="rgba(255,255,255,0.1)"
                    borderRadius="full"
                    p="1"
                    align="center"
                    border="1px solid rgba(255,255,255,0.05)"
                >
                    <Flex
                        as="button"
                        onClick={() => toggleLang('en')}
                        px={{ base: 3, md: 4 }}
                        py={1}
                        borderRadius="full"
                        bg={!isAr ? accentColor : "transparent"}
                        color={!isAr ? darkTextColor : "whiteAlpha.800"}
                        fontWeight="bold"
                        fontSize={{ base: "xs", md: "sm" }}
                        boxShadow={!isAr ? "sm" : "none"}
                        transition="all 0.3s ease"
                    >
                        EN
                    </Flex>
                    <Flex
                        as="button"
                        onClick={() => toggleLang('ar')}
                        px={{ base: 3, md: 4 }}
                        py={1}
                        borderRadius="full"
                        bg={isAr ? accentColor : "transparent"}
                        color={isAr ? darkTextColor : "whiteAlpha.800"}
                        fontWeight="bold"
                        fontSize={{ base: "xs", md: "sm" }}
                        boxShadow={isAr ? "sm" : "none"}
                        transition="all 0.3s ease"
                    >
                        عربي
                    </Flex>
                </Flex>
            </Flex>

            <Flex
                as={motion.div}
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
                h="100vh"
                align="center"
                justify="center"
                direction="column"
                textAlign="center"
                bgImage={`url(${ShipsImg})`}
                bgSize="cover"
                bgPosition="center"
                position="relative"
                px={6}
                py={{ base: 20, md: 32 }}
            >
                <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    bg="linear-gradient(to bottom, rgba(10,25,47,0.85) 0%, rgba(5,10,30,0.95) 100%)"
                    zIndex="0"
                />

                <Flex direction="column" zIndex="1" maxW="1000px" align="center">
                    <MotionText
                        variants={fadeInUp}
                        fontSize={{ base: "4xl", md: "5xl", lg: "7xl" }}
                        fontWeight="900"
                        lineHeight="1.2"
                        mb={6}
                        color="white"
                        textShadow="0px 0px 20px rgba(0,0,0,0.5)"
                    >
                        {isAr ? (
                            <>
                                خبير في <Text as="span" bgGradient={`linear(to-r, ${accentColor}, teal.300)`} bgClip="text">التوريد والشحن</Text> من الصين
                            </>
                        ) : (
                            <>
                                Expert in <Text as="span" bgGradient={`linear(to-r, ${accentColor}, teal.300)`} bgClip="text">Sourcing & Shipping</Text> from China
                            </>
                        )}
                    </MotionText>

                    <MotionText
                        variants={fadeInUp}
                        fontSize={{ base: "md", md: "xl", lg: "2xl" }}
                        color="gray.300"
                        mb={10}
                        maxW={{ base: "90%", md: "800px" }}
                        mx="auto"
                        lineHeight="1.8"
                        fontWeight="400"
                    >
                        {t.hero.subheadline}
                    </MotionText>

                    <MotionBox variants={fadeInUp}>
                        <Button
                            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                            size={{ base: "lg", md: "lg" }}
                            bg={accentColor}
                            color={darkTextColor}
                            _hover={{
                                bg: "#a3e662",
                                transform: "translateY(-2px)",
                                boxShadow: "0px 10px 25px rgba(178, 255, 107, 0.4)"
                            }}
                            _active={{ bg: "#94d150" }}
                            px={{ base: 8, md: 12 }}
                            py={{ base: 6, md: 8 }}
                            fontSize={{ base: "lg", md: "xl" }}
                            fontWeight="bold"
                            borderRadius="full"
                            transition="all 0.3s ease"
                        >
                            {t.hero.cta}
                        </Button>
                    </MotionBox>
                </Flex>
            </Flex>

            <Flex
                as={motion.div}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={staggerContainer}
                justify="center"
                mt={{ base: "-40px", md: "-80px" }}
                px={{ base: 4, md: 8 }}
                position="relative"
                zIndex="2"
            >
                <Flex
                    wrap="wrap"
                    gap={{ base: 4, md: 6 }}
                    justify="center"
                    w="full"
                    maxW="1200px"
                >
                    {t.stats.map((stat, idx) => (
                        <Flex
                            key={idx}
                            as={motion.div}
                            variants={fadeInUp}
                            direction="column"
                            align="center"
                            justify="center"
                            p={{ base: 6, md: 8 }}
                            bg="rgba(17, 34, 64, 0.9)"
                            backdropFilter="blur(16px)"
                            border={glassBorder}
                            borderRadius="2xl"
                            w={{ base: "calc(50% - 16px)", md: "45%", lg: "22%" }}
                            minW={{ base: "140px", md: "220px" }}
                            flex={{ base: "none", md: "1" }}
                            boxShadow="0px 15px 30px rgba(0,0,0,0.3)"
                            transition="all 0.3s ease"
                            whileHover={{ y: -10, borderColor: accentColor }}
                        >
                            <Icon as={stat.icon} w={{ base: 6, md: 10 }} h={{ base: 6, md: 10 }} color={accentColor} mb={4} />
                            <Text fontSize={{ base: "xl", md: "4xl" }} fontWeight="800" color="white" mb={1}>
                                <AnimatedCounter to={stat.numeric} />{stat.suffix}
                            </Text>
                            <Text fontSize={{ base: "xs", md: "md" }} color="gray.400" letterSpacing="wide" textAlign="center">
                                {stat.label}
                            </Text>
                        </Flex>
                    ))}
                </Flex>
            </Flex>

            <Flex
                as={motion.div}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                variants={staggerContainer}
                direction="column"
                py={{ base: 16, md: 32 }}
                px={{ base: 4, md: 16 }}
                align="center"
                bg="transparent"
            >
                <MotionText
                    variants={fadeInUp}
                    fontSize={{ base: "3xl", md: "5xl" }}
                    fontWeight="800"
                    mb={{ base: 10, md: 16 }}
                    color="white"
                    position="relative"
                    textAlign="center"
                    _after={{
                        content: '""',
                        display: 'block',
                        width: '60px',
                        height: '4px',
                        bg: accentColor,
                        margin: '20px auto 0',
                        borderRadius: 'full'
                    }}
                >
                    {t.services.title}
                </MotionText>

                <Flex
                    direction={{ base: "column", lg: "row" }}
                    wrap="wrap"
                    gap={{ base: 6, md: 8 }}
                    justify="center"
                    maxW="1300px"
                    w="full"
                >
                    {t.services.list.map((service, idx) => (
                        <Flex
                            key={idx}
                            as={motion.div}
                            variants={fadeInUp}
                            direction="column"
                            align="start"
                            p={{ base: 6, md: 8 }}
                            bg={glassBg}
                            backdropFilter="blur(10px)"
                            border={glassBorder}
                            borderRadius="2xl"
                            flex={{ base: "none", md: "1" }}
                            w={{ base: "100%", md: "calc(50% - 16px)", lg: "400px" }}
                            transition="all 0.4s ease"
                            _hover={{ bg: "rgba(255,255,255,0.05)", transform: "translateY(-10px)", border: `1px solid ${accentColor}` }}
                            boxShadow="0px 10px 40px rgba(0,0,0,0.2)"
                            position="relative"
                            overflow="hidden"
                        >
                            <Icon
                                as={service.icon}
                                position="absolute"
                                bottom="-20px"
                                right={!isAr ? "-20px" : "unset"}
                                left={isAr ? "-20px" : "unset"}
                                boxSize={{ base: "80px", md: "120px" }}
                                color={accentColor}
                                opacity="0.1"
                                transform="rotate(-15deg)"
                                zIndex="0"
                            />

                            <Box position="relative" zIndex="1" w="full">
                                <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="700" mb={4} color="white">
                                    {service.title}
                                </Text>

                                <List spacing={3} w="full">
                                    {service.items.map((item, i) => (
                                        <ListItem key={i} display="flex" alignItems="center">
                                            <ListIcon as={FaCheckCircle} color={accentColor} />
                                            <Text color="gray.300" fontSize={{ base: "md", md: "lg" }}>{item}</Text>
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        </Flex>
                    ))}
                </Flex>
            </Flex>

            <Flex
                as={motion.div}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                direction={{ base: 'column', lg: 'row' }}
                align="center"
                justify="center"
                py={{ base: 16, md: 24 }}
                px={{ base: 4, md: 16, lg: 24 }}
                bg="rgba(0,0,0,0.2)"
                gap={{ base: 10, lg: 20 }}
            >
                <Flex
                    direction="column"
                    flex="1"
                    textAlign={{ base: "center", lg: "start" }}
                    align={{ base: "center", lg: "start" }}
                    mb={{ base: 8, lg: 0 }}
                >
                    <Text fontSize="sm" color={accentColor} fontWeight="bold" letterSpacing="widest" mb={4} textTransform="uppercase">
                        {t.whyUs.subtitle}
                    </Text>
                    <Text fontSize={{ base: "3xl", md: "5xl" }} fontWeight="800" mb={8} color="white" lineHeight="1.2">
                        {t.whyUs.title}
                    </Text>
                    <Text fontSize={{ base: "md", md: "xl" }} lineHeight="1.8" color="gray.300" mb={8}>
                        {t.whyUs.text}
                    </Text>
                    <Flex gap={4} align="center" wrap="wrap" justify={{ base: "center", lg: "start" }}>
                        <Icon as={FaCheckCircle} color={accentColor} w={5} h={5} />
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" color="white">{t.whyUs.badges[0]}</Text>
                        <Box w="1px" h="20px" bg="gray.600" display={{ base: "none", sm: "block" }} />
                        <Icon as={FaCheckCircle} color={accentColor} w={5} h={5} />
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" color="white">{t.whyUs.badges[1]}</Text>
                        <Box w="1px" h="20px" bg="gray.600" display={{ base: "none", sm: "block" }} />
                        <Icon as={FaCheckCircle} color={accentColor} w={5} h={5} />
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" color="white">{t.whyUs.badges[2]}</Text>
                    </Flex>
                </Flex>

                <Flex flex="1" justify="center" align="center" w="100%">
                    <Image
                        src={MapImg}
                        alt="Map Coverage"
                        borderRadius="2xl"
                        boxShadow="0px 20px 50px rgba(0,0,0,0.5)"
                        maxW="600px"
                        w="100%"
                        objectFit="cover"
                        border="1px solid rgba(255,255,255,0.05)"
                        opacity="0.9"
                        filter="contrast(1.1)"
                    />
                </Flex>
            </Flex>

            <Flex
                id="contact"
                py={{ base: 12, md: 20 }}
                direction="column"
                align="center"
                justify="center"
                bgGradient="linear(to-t, gray.900, rgba(15, 23, 42, 0))"
                borderTop="1px solid rgba(255,255,255,0.05)"
                px={4}
            >
                <Text
                    fontSize={{ base: "2xl", md: "5xl" }}
                    mb={12}
                    fontWeight="800"
                    color="white"
                    textAlign="center"
                    maxW="900px"
                >
                    {t.footer.title}
                </Text>

                <Flex
                    wrap="wrap"
                    gap={{ base: 4, md: 6 }}
                    justify="center"
                    maxW="1000px"
                    w="full"
                    mb={16}
                >
                    {contactActions.map((action, i) => (
                        <Flex
                            key={i}
                            onClick={() => window.open(action.href, '_blank')}
                            align="center"
                            justify="center"
                            gap={3}
                            px={{ base: 6, md: 8 }}
                            py={{ base: 3, md: 4 }}
                            w={{ base: "100%", sm: "auto" }}
                            borderRadius="full"
                            border="1px solid"
                            borderColor="whiteAlpha.300"
                            bg="transparent"
                            color="white"
                            transition="all 0.3s ease"
                            cursor="pointer"
                            _hover={{
                                bg: accentColor,
                                color: darkTextColor,
                                borderColor: accentColor,
                                transform: "translateY(-5px)",
                                boxShadow: `0px 10px 20px rgba(178, 255, 107, 0.3)`
                            }}
                        >
                            <Icon as={action.icon} w={5} h={5} />
                            <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold">{action.label}</Text>
                        </Flex>
                    ))}
                </Flex>

                <Divider borderColor="whiteAlpha.100" maxW="400px" mb={8} />

                <Text fontSize="sm" color="gray.500" textAlign="center">
                    {t.footer.rights}
                </Text>
            </Flex>

        </Box>
    );
};

export default Home;
